import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Autocomplete,
  Paper,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { AppDatePicker } from '../../common/AppDatePicker';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import { useTranslation } from 'react-i18next';
import GuideButton from '../../common/guide/GuideButton';

interface BankToBankTransferModalProps {
  open: boolean;
  onClose: () => void;
  sourceBankAccount: SourceBankAccount | null;
  companyId: string;
  onSuccess?: () => void;
  /** Doluysa hedef olarak yalnız bu şubeye bağlı hesaplar listelenir (şube yöneticisi). */
  branchId?: string | null;
}

interface SourceBankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  balance: number;
  currency: string;
  // Alt hesap bilgileri (eğer kaynak alt hesapsa)
  isSubAccount?: boolean;
  subAccountId?: string;
  subAccountName?: string;
}

interface BankAccount {
  id: string;
  bankName: string;
  accountNumber: string;
  currency: string;
  balance: number;
  subAccounts?: BankSubAccount[];
}

interface BankSubAccount {
  id: string;
  accountName: string;
  accountNumber: string;
  currency: string;
  balance: number;
  parentBankAccountId: string;
}

const BankToBankTransferModal: React.FC<BankToBankTransferModalProps> = ({
  open,
  onClose,
  sourceBankAccount,
  companyId,
  onSuccess,
  branchId = null,
}) => {
  const { i18n } = useTranslation('accounting');
  const isTr = i18n.language?.toLowerCase().startsWith('tr');
  const localeCode = isTr ? 'tr-TR' : 'en-US';
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  // State tanımlamaları
  const [allBankAccounts, setAllBankAccounts] = useState<BankAccount[]>([]);
  const [selectedTargetBank, setSelectedTargetBank] = useState<BankAccount | null>(null);
  const [selectedTargetSubAccount, setSelectedTargetSubAccount] = useState<BankSubAccount | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  // ✅ Geçmiş tarihli işlem: toggle açıldığında tarih seçici görünür.
  // İşaretli ve tarih girilmişse hareket kaydı TAMAMEN o tarihe yazılır (Date + CreatedAt).
  const [isBackdated, setIsBackdated] = useState(false);
  const [transactionDate, setTransactionDate] = useState<string>(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  });
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Para birimi sembolünü döndür
  const getCurrencySymbol = (currency: string): string => {
    switch(currency) {
      case 'TRY': return '₺';
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      default: return currency;
    }
  };

  // Binlik ayraç ile sayı formatlama (ondalık destekli)
  const formatNumberWithThousandsSeparator = (value: string): string => {
    // Sadece rakamları ve virgülü tut
    const cleanValue = value.replace(/[^\d,]/g, '');
    
    // Birden fazla virgül varsa sadece ilkini tut
    const commaCount = (cleanValue.match(/,/g) || []).length;
    if (commaCount > 1) {
      const firstCommaIndex = cleanValue.indexOf(',');
      const beforeComma = cleanValue.substring(0, firstCommaIndex);
      const afterComma = cleanValue.substring(firstCommaIndex + 1).replace(/,/g, '');
      return formatNumberWithThousandsSeparator(beforeComma + ',' + afterComma);
    }
    
    // Virgülden önceki ve sonraki kısımları ayır
    const parts = cleanValue.split(',');
    const integerPart = parts[0];
    let decimalPart = parts.length > 1 ? parts[1] : null; // null = virgül yok, '' = virgül var ama rakam yok
    
    // Ondalık kısmı maksimum 2 basamak ile sınırla
    if (decimalPart && decimalPart.length > 2) {
      decimalPart = decimalPart.substring(0, 2);
    }
    
    // Binlik ayraçlarını ekle (integer kısmına)
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    
    // Decimal varsa ekle (virgül girilmişse, rakam girilmese bile virgülü göster)
    return decimalPart !== null ? `${formattedInteger},${decimalPart}` : formattedInteger;
  };

  // Formatlanmış sayıyı parse et
  const parseFormattedNumber = (value: string): number => {
    if (!value) return 0;
    // Binlik ayraçları kaldır, virgülü noktaya çevir
    const cleanValue = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(cleanValue) || 0;
  };

  // Miktar değişimini handle et
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formatted = formatNumberWithThousandsSeparator(value);
    setAmount(formatted);
  };

  // Mevcut alt hesapları filtrele
  const availableSubAccounts = selectedTargetBank?.subAccounts?.filter(
    sub => sub.currency === sourceBankAccount?.currency
  ) || [];

  // Banka hesaplarını yükle
  useEffect(() => {
    if (open && companyId && sourceBankAccount) {
      loadBankAccounts();
    }
  }, [open, companyId, sourceBankAccount]);

  const loadBankAccounts = async () => {
    if (!sourceBankAccount) return;
    
    setLoadingAccounts(true);
    setError(null);
    
    try {
      const response = await accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY);
      const sqlBankAccounts = response.data.data || response.data;
      
      if (!sqlBankAccounts || sqlBankAccounts.length === 0) {
        setAllBankAccounts([]);
        setLoadingAccounts(false);
        return;
      }
      
      // SQL verilerini frontend formatına çevir
      const bankAccountList: BankAccount[] = sqlBankAccounts
        .filter((data: any) => {
          // Kaynak hesabı hariç tut
          const isSameAsSource = sourceBankAccount.isSubAccount 
            ? data.id.toString() === sourceBankAccount.id
            : data.id.toString() === sourceBankAccount.id;
          
          // Ana hesapları filtrele (parentBankAccountId olmayanlar)
          const isMainAccount = !data.parentBankAccountId;
          
          // Aynı para biriminde olanları filtrele
          const isSameCurrency = data.currency === sourceBankAccount.currency;
          
          const isInBranch = !branchId || data.branchId === branchId;
          
          return !isSameAsSource && isMainAccount && isSameCurrency && isInBranch;
        })
        .map((data: any) => {
          // Alt hesapları dönüştür
          const subAccountsArray: BankSubAccount[] = [];
          if (data.subAccounts && Array.isArray(data.subAccounts)) {
            data.subAccounts.forEach((subAccount: any) => {
              // Kaynak alt hesabı hariç tut
              const isSameAsSourceSubAccount = sourceBankAccount.isSubAccount 
                && subAccount.id.toString() === sourceBankAccount.subAccountId;
              
              if (!isSameAsSourceSubAccount && subAccount.currency === sourceBankAccount.currency) {
                subAccountsArray.push({
                  id: `${subAccount.id}`,
                  accountName: subAccount.accountName || '',
                  accountNumber: subAccount.accountNumber || '',
                  currency: subAccount.currency || 'TRY',
                  balance: parseFloat(subAccount.balance) || 0,
                  parentBankAccountId: `${data.id}`
                });
              }
            });
          }

          return {
            id: `${data.id}`,
            bankName: data.bankName || '',
            accountNumber: data.accountNumber || '',
            currency: data.currency || 'TRY',
            balance: parseFloat(data.balance) || 0,
            subAccounts: subAccountsArray.length > 0 ? subAccountsArray : undefined
          };
        });
      
      setAllBankAccounts(bankAccountList);
      setLoadingAccounts(false);
    } catch (error: any) {
      console.error('❌ [BANK TRANSFER] Banka hesapları yüklenirken hata:', error);
      setError(tt('Banka hesapları yüklenirken bir hata oluştu.', 'An error occurred while loading bank accounts.'));
      setLoadingAccounts(false);
    }
  };

  // Ana banka hesabı değiştiğinde alt hesap seçimini sıfırla
  useEffect(() => {
    setSelectedTargetSubAccount(null);
  }, [selectedTargetBank]);

  // Seçili hedef hesabın güncel bakiyesini al
  const getSelectedTargetBalance = (): number => {
    if (!selectedTargetBank) return 0;
    
    // Alt hesap seçiliyse alt hesabın bakiyesi
    if (selectedTargetSubAccount) {
      return selectedTargetSubAccount.balance;
    }
    
    // Ana hesap seçiliyse ana hesabın bakiyesi
    return selectedTargetBank.balance;
  };

  // Form sıfırlama
  const resetForm = () => {
    setSelectedTargetBank(null);
    setSelectedTargetSubAccount(null);
    setAmount('');
    setDescription('');
    setError(null);
    setSuccess(false);
    setSuccessMessage('');
    // ✅ Geçmiş tarihli işlem alanını sıfırla (varsayılan: kapalı / bugün)
    setIsBackdated(false);
    const today = new Date();
    setTransactionDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`);
  };

  // Modal kapanırken form sıfırla
  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  // Transfer işlemini gerçekleştir
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!sourceBankAccount || !selectedTargetBank) {
      setError(tt('Lütfen hedef banka hesabı seçin', 'Please select a target bank account'));
      return;
    }
    
    const amountValue = parseFormattedNumber(amount);
    
    if (amountValue <= 0) {
      setError(tt('Lütfen geçerli bir miktar girin', 'Please enter a valid amount'));
      return;
    }
    
    if (amountValue > sourceBankAccount.balance) {
      setError(tt('Yetersiz bakiye!', 'Insufficient balance!'));
      return;
    }

    // ✅ Geçmiş tarihli işlem: işaretliyse seçilen tarihi hesapla.
    // Hareket kaydı TAMAMEN bu tarihe yazılacak (backend Date + CreatedAt'i bu değere ayarlar).
    let backdatedIso: string | null = null;
    if (isBackdated) {
      const parts = transactionDate.split('-');
      const selected = parts.length === 3
        ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]), 12, 0, 0)
        : new Date(NaN);
      if (isNaN(selected.getTime())) {
        setError(tt('Geçerli bir işlem tarihi seçiniz.', 'Please select a valid transaction date.'));
        return;
      }
      // Gün kayması yaşanmaması için öğlen (12:00) sabitlenir.
      backdatedIso = selected.toISOString();
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Tarih ve saat bilgisi
      const now = new Date();
      // ✅ Belge no tarih kısmı: geçmiş tarihliyse seçilen tarihe göre üretilir (bugüne dair iz taşımaz)
      const dateStr = (isBackdated && transactionDate)
        ? transactionDate.replace(/-/g, '')
        : now.toISOString().slice(0, 10).replace(/-/g, '');
      const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
      const documentNo = `BBT-${dateStr}-${timeStr}`; // Bank to Bank Transfer
      
      // Açıklama oluştur
      const sourceAccountName = sourceBankAccount.isSubAccount 
        ? `${sourceBankAccount.bankName} - ${sourceBankAccount.subAccountName}`
        : `${sourceBankAccount.bankName} - ${sourceBankAccount.accountNumber}`;
      
      const targetAccountName = selectedTargetSubAccount 
        ? `${selectedTargetBank.bankName} - ${selectedTargetSubAccount.accountName}`
        : `${selectedTargetBank.bankName} - ${selectedTargetBank.accountNumber}`;
      
      const transferDescription = description || `${sourceAccountName} → ${targetAccountName} ${tt('banka transferi', 'bank transfer')}`;
      
      // Kullanıcı email'i al
      const userEmail = ownAuthSession.getCurrentEmail() || 'system';
      
      // API isteği hazırla
      const requestBody: Record<string, any> = {
        sourceBankAccountId: sourceBankAccount.isSubAccount ? parseInt(sourceBankAccount.id) : parseInt(sourceBankAccount.id),
        sourceBankSubAccountId: sourceBankAccount.isSubAccount ? parseInt(sourceBankAccount.subAccountId!) : null,
        targetBankAccountId: parseInt(selectedTargetBank.id),
        targetBankSubAccountId: selectedTargetSubAccount ? parseInt(selectedTargetSubAccount.id) : null,
        amount: amountValue,
        description: transferDescription,
        documentNo: documentNo,
        createdBy: userEmail
      };
      // Yalnızca geçmiş tarihli işlem seçildiyse tarihi gönder; aksi halde backend bugünü kullanır.
      if (backdatedIso) {
        requestBody.transactionDate = backdatedIso;
      }
      
      console.log('💸 [BANK TRANSFER] İstek gönderiliyor:', requestBody);
      
      // API çağrısı yap
      const response = await accountingApi.post(ACCOUNTING_API.BANK_ACCOUNT_TRANSFER, requestBody);
      const result = response.data;
      
      console.log('✅ [BANK TRANSFER] Transfer başarılı:', result);
      
      // Başarı mesajını hazırla
      const formattedAmount = amountValue.toLocaleString(localeCode, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      
      const successMsg = `✅ ${formattedAmount} ${getCurrencySymbol(sourceBankAccount.currency)} ${targetAccountName} hesabına aktarıldı.`;
      
      setSuccess(true);
      setSuccessMessage(successMsg);
      
      // 1.5 saniye sonra modalı kapat
      setTimeout(() => {
        setSuccess(false);
        if (onSuccess) onSuccess();
        onClose();
        resetForm();
      }, 1500);
      
    } catch (error: any) {
      console.error('❌ [BANK TRANSFER] Banka transferi hatası:', error);
      setError(error.response?.data?.message || error.message || tt('Banka transferi sırasında bir hata oluştu', 'An error occurred during bank transfer'));
    } finally {
      setLoading(false);
    }
  };

  if (!sourceBankAccount) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHorizIcon />
          <Typography variant="h6">{tt('Banka Transferi', 'Bank Transfer')}</Typography>
          <GuideButton guideKey="finance.bankToBankTransfer" color="inherit" size="small" />
        </Box>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          disabled={loading}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 3 }}>
          {/* Başarı Durumu - Tam Ekran */}
          {success ? (
            <Box textAlign="center" p={3}>
              <CheckCircleOutlineIcon color="success" style={{ fontSize: 60 }} />
              <Typography variant="h6" color="success" gutterBottom sx={{ mt: 2 }}>
                {successMessage}
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Hata Mesajı */}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* Kaynak Hesap Bilgisi */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'info.50', borderColor: 'info.main' }}>
            <Typography variant="caption" color="textSecondary" gutterBottom>
              {tt('Kaynak Hesap', 'Source Account')}
            </Typography>
            <Typography variant="h6" gutterBottom>
              {sourceBankAccount.isSubAccount 
                ? `${sourceBankAccount.bankName} - ${sourceBankAccount.subAccountName} (${tt('Alt Hesap', 'Sub Account')})`
                : `${sourceBankAccount.bankName} - ${sourceBankAccount.accountNumber}`
              }
            </Typography>
            <Typography 
              variant="body1" 
              color="info.main"
              fontWeight="bold"
            >
              {tt('Mevcut Bakiye', 'Current Balance')}: {sourceBankAccount.balance.toLocaleString(localeCode, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })} {getCurrencySymbol(sourceBankAccount.currency)}
            </Typography>
          </Paper>

          {/* Ana Hedef Banka Hesabı Seçimi */}
          {loadingAccounts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <Autocomplete
                options={allBankAccounts}
                getOptionLabel={(option) => 
                  `${option.bankName} - ${option.accountNumber} (${tt('Bakiye', 'Balance')}: ${option.balance.toLocaleString(localeCode, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })} ${getCurrencySymbol(option.currency)})`
                }
                value={selectedTargetBank}
                onChange={(event, newValue) => {
                  setSelectedTargetBank(newValue);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={loading || loadingAccounts}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={tt('Hedef Banka Hesabı', 'Target Bank Account')}
                    placeholder={tt('Hedef banka hesabı seç...', 'Select target bank account...')}
                    required
                    helperText={tt(`Sadece ${getCurrencySymbol(sourceBankAccount.currency)} para birimindeki hesaplar gösteriliyor.`, `Only accounts in ${getCurrencySymbol(sourceBankAccount.currency)} currency are shown.`)}
                  />
                )}
                noOptionsText={tt('Uygun banka hesabı bulunamadı', 'No suitable bank account found')}
                sx={{ mb: 2 }}
              />

              {/* Alt Hesap Seçimi (Opsiyonel) */}
              {selectedTargetBank && availableSubAccounts.length > 0 && (
                <Autocomplete
                  options={availableSubAccounts}
                  getOptionLabel={(option) => 
                    `${option.accountName} - ${option.accountNumber} (${tt('Bakiye', 'Balance')}: ${option.balance.toLocaleString(localeCode, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })} ${getCurrencySymbol(option.currency)})`
                  }
                  value={selectedTargetSubAccount}
                  onChange={(event, newValue) => {
                    setSelectedTargetSubAccount(newValue);
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disabled={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={tt('Alt Hesap (Opsiyonel)', 'Sub Account (Optional)')}
                      placeholder={tt('Alt hesap seç...', 'Select sub account...')}
                      helperText={tt('Alt hesap seçilmezse, transfer ana banka hesabına yapılacaktır.', 'If no sub account is selected, transfer will be made to the main bank account.')}
                    />
                  )}
                  noOptionsText={tt('Alt hesap bulunamadı', 'No sub account found')}
                  sx={{ mb: 2 }}
                />
              )}

              {/* Seçili Hedef Hesap Bilgisi */}
              {selectedTargetBank && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'success.50', borderColor: 'success.main' }}>
                  <Typography variant="caption" color="textSecondary" gutterBottom>
                    {tt('Hedef Hesap', 'Target Account')}
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {selectedTargetSubAccount 
                      ? `${selectedTargetBank.bankName} - ${selectedTargetSubAccount.accountName} (${tt('Alt Hesap', 'Sub Account')})`
                      : `${selectedTargetBank.bankName} - ${selectedTargetBank.accountNumber}`
                    }
                  </Typography>
                  <Typography 
                    variant="body1" 
                    color="success.main"
                    fontWeight="bold"
                  >
                    {tt('Mevcut Bakiye', 'Current Balance')}: {getSelectedTargetBalance().toLocaleString(localeCode, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })} {getCurrencySymbol(sourceBankAccount.currency)}
                  </Typography>
                </Paper>
              )}
            </>
          )}

          {/* Miktar */}
          <TextField
            fullWidth
            label={tt('Miktar', 'Amount')}
            type="text"
            value={amount}
            onChange={handleAmountChange}
            placeholder={tt('0,00', '0.00')}
            required
            disabled={loading || loadingAccounts || !selectedTargetBank}
            inputProps={{ 
              style: { textAlign: 'right', fontSize: '1.2rem', fontWeight: 'bold' }
            }}
            InputProps={{
              endAdornment: <Typography variant="body1" color="textSecondary">{getCurrencySymbol(sourceBankAccount.currency)}</Typography>
            }}
            sx={{ mb: 2 }}
          />

          {/* Açıklama */}
          <TextField
            fullWidth
            label={tt('Açıklama (Opsiyonel)', 'Description (Optional)')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={tt('Transfer açıklaması...', 'Transfer description...')}
            multiline
            rows={2}
            disabled={loading || loadingAccounts}
            sx={{ mb: 2 }}
          />

          {/* Geçmiş Tarihli İşlem */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isBackdated}
                onChange={(e) => setIsBackdated(e.target.checked)}
                disabled={loading}
                color="primary"
              />
            }
            label={tt('Geçmiş tarihli işlem yap', 'Record as a back-dated transaction')}
          />

          {isBackdated && (
            <>
              <AppDatePicker
                label={tt('İşlem Tarihi *', 'Transaction Date *')}
                value={transactionDate}
                onChange={setTransactionDate}
                fullWidth
                required
                disabled={loading}
                disableFuture
                size="medium"
                sx={{ mt: 1, mb: 1 }}
              />
              <Alert severity="warning" sx={{ mb: 2 }}>
                {tt(
                  'Bu transfer seçtiğiniz tarihe kaydedilecektir. İşlem kaydı tamamen o tarihe ait olacak; bugüne dair hiçbir bilgi içermeyecektir.',
                  'This transfer will be recorded on the selected date. The transaction record will fully belong to that date and contain no reference to today.'
                )}
              </Alert>
            </>
          )}
            </Box>
          )}
        </DialogContent>

        {!success && (
          <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            color="inherit"
          >
            {tt('İptal', 'Cancel')}
          </Button>
          <Button 
            type="submit"
            variant="contained" 
            disabled={loading || loadingAccounts || !selectedTargetBank || !amount || parseFormattedNumber(amount) <= 0 || (isBackdated && !transactionDate)}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SwapHorizIcon />}
          >
            {loading ? tt('Transfer Yapılıyor...', 'Processing Transfer...') : tt('Transfer Yap', 'Make Transfer')}
          </Button>
        </DialogActions>
        )}
      </form>
    </Dialog>
  );
};

export default BankToBankTransferModal;

